import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Upload,
  Plus,
  Trash2,
  Edit3,
  Download,
  Search,
  IndianRupee,
  Truck,
  FileSpreadsheet,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Globe,
  Package,
  Scale,
  History,
  Clock,
  ArrowRight,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

// Helper function to format date as DDMMYY
const formatDateDDMMYY = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}${month}${year}`;
  } catch {
    return "N/A";
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientShippingRates({ clientCode, clientName, onClose }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shippingRates, setShippingRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterService, setFilterService] = useState("all");
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showUploadHistoryDialog, setShowUploadHistoryDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showEditItemDialog, setShowEditItemDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingHistory, setUploadingHistory] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [historyFile, setHistoryFile] = useState(null);
  const historyFileInputRef = useRef(null);
  
  // Form state for add/edit
  const [formData, setFormData] = useState({
    country: "",
    service_type: "",
    weight_from_kg: "",
    weight_to_kg: "",
    price: ""
  });

  // Fetch shipping rates
  const fetchShippingRates = useCallback(async () => {
    if (!clientCode) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setShippingRates(data.shipping_rates || []);
        setCountries(data.countries || []);
        setServices(data.services || []);
      } else {
        toast.error(data.detail || "Failed to fetch shipping rates");
      }
    } catch (error) {
      console.error("Error fetching shipping rates:", error);
      toast.error("Failed to fetch shipping rates");
    } finally {
      setLoading(false);
    }
  }, [clientCode, token]);

  useEffect(() => {
    fetchShippingRates();
  }, [fetchShippingRates]);

  // Fetch price history for an item
  const fetchPriceHistory = async (item) => {
    setHistoryItem(item);
    setShowHistoryDialog(true);
    setLoadingHistory(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates/${item.id}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      
      if (response.ok) {
        const history = data.price_history || [];
        setPriceHistory(history);
        
        // Calculate effective current price based on today's date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let effectivePrice = item.price; // Default to stored price
        let effectiveRecord = null;
        
        for (const h of history) {
          const fromDate = h.effective_from ? new Date(h.effective_from) : null;
          const toDate = h.effective_to ? new Date(h.effective_to) : null;
          
          if (fromDate) fromDate.setHours(0, 0, 0, 0);
          if (toDate) toDate.setHours(23, 59, 59, 999);
          
          // Check if today falls within this date range
          const afterFrom = !fromDate || today >= fromDate;
          const beforeTo = !toDate || today <= toDate;
          
          if (afterFrom && beforeTo) {
            effectivePrice = h.price;
            effectiveRecord = h;
            break; // First matching record (most recent)
          }
        }
        
        // Update historyItem with effective price
        setHistoryItem({
          ...item,
          effective_price: effectivePrice,
          effective_from: effectiveRecord?.effective_from,
          effective_to: effectiveRecord?.effective_to,
          is_from_history: !!effectiveRecord
        });
      } else {
        toast.error("Failed to fetch price history");
        setPriceHistory([]);
      }
    } catch (error) {
      console.error("Error fetching price history:", error);
      toast.error("Failed to fetch price history");
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Download template Excel file
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-shipping-rates/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Shipping_Rates_Template_${clientCode}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Template downloaded successfully");
      } else {
        toast.error("Failed to download template");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download template");
    }
  };

  // Download shipping rate history template
  const handleDownloadHistoryTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-shipping-history/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${clientCode}_shipping_history_template.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Template downloaded");
      } else {
        toast.error("Failed to download template");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  // Upload shipping rate history file
  const handleUploadHistory = async () => {
    if (!historyFile) {
      toast.error("Please select a file");
      return;
    }
    
    setUploadingHistory(true);
    
    try {
      const formDataObj = new FormData();
      formDataObj.append("file", historyFile);
      
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rate-history/upload`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formDataObj
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Shipping history uploaded: ${data.added_count} added, ${data.updated_count} updated`);
        setShowUploadHistoryDialog(false);
        setHistoryFile(null);
        if (historyFileInputRef.current) historyFileInputRef.current.value = "";
      } else {
        toast.error(data.detail || "Failed to upload shipping history");
      }
    } catch (error) {
      console.error("Error uploading shipping history:", error);
      toast.error("Failed to upload shipping history");
    } finally {
      setUploadingHistory(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates/upload?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Uploaded: ${data.added_count} added, ${data.updated_count} updated`);
        setShowUploadDialog(false);
        fetchShippingRates();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Add shipping rate
  const handleAddItem = async () => {
    if (!formData.country.trim()) {
      toast.error("Country is required");
      return;
    }
    if (!formData.service_type.trim()) {
      toast.error("Service type is required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates/item`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          weight_from_kg: parseFloat(formData.weight_from_kg) || 0,
          weight_to_kg: parseFloat(formData.weight_to_kg) || 0,
          price: parseFloat(formData.price) || 0
        })
      });
      
      if (response.ok) {
        toast.success("Shipping rate added");
        setShowAddItemDialog(false);
        setFormData({ country: "", service_type: "", weight_from_kg: "", weight_to_kg: "", price: "" });
        fetchShippingRates();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to add rate");
      }
    } catch (error) {
      console.error("Error adding rate:", error);
      toast.error("Failed to add rate");
    }
  };

  // Update shipping rate
  const handleUpdateItem = async () => {
    if (!editingItem?.id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates/${editingItem.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          weight_from_kg: parseFloat(formData.weight_from_kg) || 0,
          weight_to_kg: parseFloat(formData.weight_to_kg) || 0,
          price: parseFloat(formData.price) || 0
        })
      });
      
      if (response.ok) {
        toast.success("Shipping rate updated");
        setShowEditItemDialog(false);
        setEditingItem(null);
        fetchShippingRates();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update rate");
      }
    } catch (error) {
      console.error("Error updating rate:", error);
      toast.error("Failed to update rate");
    }
  };

  // Delete shipping rate
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Delete this shipping rate?")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Shipping rate deleted");
        fetchShippingRates();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete rate");
      }
    } catch (error) {
      console.error("Error deleting rate:", error);
      toast.error("Failed to delete rate");
    }
  };

  // Delete all shipping rates
  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${shippingRates.length} shipping rates for ${clientCode}? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/shipping-rates`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchShippingRates();
      } else {
        toast.error(data.detail || "Failed to delete shipping rates");
      }
    } catch (error) {
      console.error("Error deleting shipping rates:", error);
      toast.error("Failed to delete shipping rates");
    }
  };

  // Filter shipping rates
  const filteredRates = shippingRates.filter(item => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = 
        item.country?.toLowerCase().includes(term) ||
        item.service_type?.toLowerCase().includes(term);
      if (!matches) return false;
    }
    // Country filter
    if (filterCountry !== "all" && item.country !== filterCountry) return false;
    // Service filter
    if (filterService !== "all" && item.service_type !== filterService) return false;
    return true;
  });

  // Open edit dialog
  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      country: item.country || "",
      service_type: item.service_type || "",
      weight_from_kg: item.weight_from_kg || "",
      weight_to_kg: item.weight_to_kg || "",
      price: item.price || ""
    });
    setShowEditItemDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value || 0);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            DP CLIENT Shipping Rates for {clientCode}
          </h3>
          <p className="text-sm text-muted-foreground">
            {clientName && `(${clientName})`} • {shippingRates.length} rates • {countries.length} countries
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchShippingRates}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap gap-2 items-center justify-between bg-slate-50 p-3 rounded-lg">
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => setShowUploadDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Upload className="w-4 h-4 mr-1" />
            Upload Excel
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowUploadHistoryDialog(true)}
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
          >
            <History className="w-4 h-4 mr-1" />
            Upload History
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadTemplate}
          >
            <Download className="w-4 h-4 mr-1" />
            Template
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddItemDialog(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Rate
          </Button>
        </div>
        
        {shippingRates.length > 0 && (
          <Button
            size="sm"
            variant="destructive"
            onClick={handleDeleteAll}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete All
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search country or service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterCountry} onValueChange={setFilterCountry}>
          <SelectTrigger className="w-40">
            <Globe className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="w-40">
            <Truck className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            {services.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-slate-500">Loading shipping rates...</span>
        </div>
      ) : filteredRates.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
          <Truck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No shipping rates found</p>
          <p className="text-sm text-slate-400 mt-1">
            {shippingRates.length === 0 
              ? "Upload an Excel file or add rates manually"
              : "Try adjusting your filters"
            }
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
          <Table>
            <TableHeader className="bg-slate-100 sticky top-0">
              <TableRow>
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="text-center">Weight Range (kg)</TableHead>
                <TableHead className="text-right">Current Rate</TableHead>
                <TableHead className="w-24 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRates.map((item, idx) => (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell className="text-center text-slate-400 text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      {item.country}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {item.service_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-mono text-sm">
                      {item.weight_from_kg} - {item.weight_to_kg} kg
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end">
                      <span className={`font-semibold ${item.is_effective_from_history ? 'text-blue-600' : 'text-green-600'}`}>
                        {formatCurrency(item.effective_price !== undefined ? item.effective_price : item.price)}
                      </span>
                      {item.is_effective_from_history && (
                        <span className="text-xs text-blue-500 flex items-center gap-0.5" title={`Effective: ${formatDateDDMMYY(item.effective_from)} - ${item.effective_to ? formatDateDDMMYY(item.effective_to) : 'Ongoing'}`}>
                          <Clock className="w-3 h-3" />
                          Date-Effective
                        </span>
                      )}
                      {item.is_effective_from_history && item.price !== item.effective_price && (
                        <span className="text-xs text-slate-400">
                          Base: {formatCurrency(item.price)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex gap-1 justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(item)}
                        className="h-7 w-7 p-0"
                        title="Edit"
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchPriceHistory(item)}
                        className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Price History"
                      >
                        <History className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteItem(item.id)}
                        className="h-7 w-7 p-0 text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Price History Dialog - Portal to document.body */}
      {createPortal(
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-2xl z-[9999]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Shipping Rate History
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Current Rate Info */}
              {historyItem && (
                <div className={`rounded-lg p-4 ${historyItem.is_from_history ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className={`text-xs ${historyItem.is_from_history ? 'text-blue-600' : 'text-green-600'}`}>Country</span>
                      <p className={`font-medium ${historyItem.is_from_history ? 'text-blue-800' : 'text-green-800'}`}>{historyItem.country}</p>
                    </div>
                    <div>
                      <span className={`text-xs ${historyItem.is_from_history ? 'text-blue-600' : 'text-green-600'}`}>Service</span>
                      <p className={`font-medium ${historyItem.is_from_history ? 'text-blue-800' : 'text-green-800'}`}>{historyItem.service_type}</p>
                    </div>
                    <div>
                      <span className={`text-xs ${historyItem.is_from_history ? 'text-blue-600' : 'text-green-600'}`}>Weight Range</span>
                      <p className={`font-medium ${historyItem.is_from_history ? 'text-blue-800' : 'text-green-800'}`}>{historyItem.weight_from_kg} - {historyItem.weight_to_kg} kg</p>
                    </div>
                    <div>
                      <span className={`text-xs ${historyItem.is_from_history ? 'text-blue-600' : 'text-green-600'}`}>
                        {historyItem.is_from_history ? 'Effective Rate (Today)' : 'Current Rate'}
                      </span>
                      <p className={`font-bold flex items-center gap-1 ${historyItem.is_from_history ? 'text-blue-700' : 'text-green-700'}`}>
                        <IndianRupee className="w-4 h-4" />
                        {(historyItem.effective_price || historyItem.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                      </p>
                      {historyItem.is_from_history && historyItem.effective_from && (
                        <p className="text-xs text-blue-600 font-mono mt-1">
                          Valid: {formatDateDDMMYY(historyItem.effective_from)} → {historyItem.effective_to ? formatDateDDMMYY(historyItem.effective_to) : 'Ongoing'}
                        </p>
                      )}
                    </div>
                  </div>
                  {historyItem.is_from_history && historyItem.price !== historyItem.effective_price && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <span className="text-xs text-slate-500">Base Rate: ₹{(historyItem.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
                    </div>
                  )}
                </div>
              )}

              {/* History List */}
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : priceHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No price history recorded yet</p>
                  <p className="text-xs mt-1">Price changes will be tracked automatically</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  <p className="text-sm font-medium text-slate-700 mb-2">Previous Prices ({priceHistory.length})</p>
                  {priceHistory.map((h, idx) => (
                    <div
                      key={h.id || idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-full">
                          <IndianRupee className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-700">
                              ₹{(h.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            </p>
                            {/* Show price change indicator compared to current price */}
                            {historyItem?.price && h.price !== historyItem.price && (
                              <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                                h.price > historyItem.price 
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {h.price > historyItem.price ? (
                                  <TrendingDown className="w-3 h-3" />
                                ) : (
                                  <TrendingUp className="w-3 h-3" />
                                )}
                                {Math.abs(((historyItem.price - h.price) / h.price) * 100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 font-mono">
                            {formatDateDDMMYY(h.effective_from)}
                            <ArrowRight className="w-3 h-3 inline mx-1" />
                            {h.effective_to ? formatDateDDMMYY(h.effective_to) : 'Current'}
                          </p>
                        </div>
                      </div>
                      {h.changed_by && (
                        <span className="text-xs text-slate-400">by {h.changed_by}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>,
        document.body
      )}

      {/* Summary */}
      {shippingRates.length > 0 && (
        <div className="flex gap-4 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-500" />
            <span>{countries.length} Countries</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-purple-500" />
            <span>{services.length} Services</span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-green-500" />
            <span>{shippingRates.length} Total Rates</span>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Upload Shipping Rates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-700 mb-1">Expected columns:</p>
              <ul className="text-blue-600 space-y-0.5 ml-3 list-disc">
                <li>Country (e.g., USA, UK, AUSTRALIA, or <strong>ALL</strong> for universal rate)</li>
                <li>Service Type (e.g., EMS, DHL, FedEx)</li>
                <li>Weight From (kg)</li>
                <li>Weight To (kg)</li>
                <li>Price (INR)</li>
              </ul>
              <p className="text-xs text-blue-500 mt-2 italic">
                💡 Country aliases work automatically (USA = US = UNITED STATES)
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="replaceExisting"
                checked={replaceExisting}
                onCheckedChange={setReplaceExisting}
              />
              <Label htmlFor="replaceExisting" className="text-sm cursor-pointer">
                Replace all existing rates (unchecked = update/add only)
              </Label>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="shipping-file-upload"
                disabled={uploading}
              />
              <label
                htmlFor="shipping-file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FileSpreadsheet className="w-10 h-10 text-slate-400 mb-2" />
                <span className="text-slate-600 font-medium">
                  {uploading ? "Uploading..." : "Click to upload Excel file"}
                </span>
                <span className="text-xs text-slate-400 mt-1">.xlsx or .xls</span>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Shipping History Dialog */}
      <Dialog open={showUploadHistoryDialog} onOpenChange={setShowUploadHistoryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Upload Shipping Rate History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-700 mb-1">Expected columns:</p>
              <ul className="text-blue-600 space-y-0.5 ml-3 list-disc">
                <li>country, service_type, weight_from_kg, weight_to_kg</li>
                <li>price (historical rate)</li>
                <li>effective_from (DD-MM-YYYY)</li>
                <li>effective_to (DD-MM-YYYY, leave blank for current)</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Use this to upload past shipping rates (last 5 years data)
              </p>
            </div>
            
            <Button variant="outline" size="sm" onClick={handleDownloadHistoryTemplate} className="w-full gap-2">
              <Download className="w-4 h-4" />
              Download History Template
            </Button>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input
                ref={historyFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setHistoryFile(e.target.files[0])}
                className="hidden"
                id="shipping-history-upload"
                disabled={uploadingHistory}
              />
              <label
                htmlFor="shipping-history-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FileSpreadsheet className="w-10 h-10 text-slate-400 mb-2" />
                <span className="text-slate-600 font-medium">
                  {historyFile ? historyFile.name : "Click to upload Excel file"}
                </span>
                <span className="text-xs text-slate-400 mt-1">.xlsx or .xls</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUploadHistoryDialog(false);
              setHistoryFile(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleUploadHistory} disabled={uploadingHistory || !historyFile} className="gap-2">
              {uploadingHistory ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadingHistory ? "Uploading..." : "Upload History"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Add Shipping Rate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value.toUpperCase() }))}
                  placeholder="e.g., USA or ALL"
                />
                <p className="text-xs text-slate-500">Use "ALL" for universal rate</p>
              </div>
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Input
                  value={formData.service_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value.toUpperCase() }))}
                  placeholder="e.g., EMS"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Weight From (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight_from_kg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_from_kg: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Weight To (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight_to_kg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_to_kg: e.target.value }))}
                  placeholder="0.5"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Price (₹) *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
            <Button onClick={handleAddItem}>Add Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={showEditItemDialog} onOpenChange={setShowEditItemDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-amber-600" />
              Edit Shipping Rate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value.toUpperCase() }))}
                  placeholder="e.g., USA or ALL"
                />
                <p className="text-xs text-slate-500">Use "ALL" for universal rate</p>
              </div>
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Input
                  value={formData.service_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value.toUpperCase() }))}
                  placeholder="e.g., EMS"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Weight From (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight_from_kg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_from_kg: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Weight To (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight_to_kg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_to_kg: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Price (₹) *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditItemDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateItem}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
